/**
 * ISSUE-1092. The create pool wizard's step 1 as tiles: one grid for the
 * league, one for the contest type. Built on the same Radix radio group
 * primitive `@sportsmet/ui`'s Radio uses, so it keeps the radiogroup contract:
 * one tab stop for the whole grid, arrow keys move the selection, and a
 * disabled ("Coming soon") tile is skipped and cannot be chosen. Selected is a
 * solid primary border plus the check badge the onboarding sports tiles draw.
 *
 * Feature local on purpose: a shared component in packages/ui would add a
 * gallery section with a visual baseline. This one is shown at
 * /gallery/create-pool-tiles and measured by an e2e geometry spec instead.
 */
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { cn, FOCUS_RING, REDUCED_MOTION_SAFE } from "@sportsmet/ui";
import { type ReactNode, useId } from "react";

export type typeCreatePoolTile = {
  value: string;
  title: string;
  subtitle: string;
  icon: ReactNode;
  isDisabled: boolean;
};

export type typeCreatePoolTileGridProps = {
  label: string;
  tiles: readonly typeCreatePoolTile[];
  value: string | null;
  onValueChange: (value: string) => void;
  testId: string;
};

export const CreatePoolTileGrid = (
  props: typeCreatePoolTileGridProps,
): ReactNode => {
  const labelId = `${useId()}-label`;

  return (
    <div className="flex w-full flex-col gap-2">
      <span id={labelId} className="text-caption text-ink">
        {props.label}
      </span>
      <RadioGroupPrimitive.Root
        aria-labelledby={labelId}
        value={props.value ?? ""}
        onValueChange={props.onValueChange}
        loop
        className="grid grid-cols-2 gap-3 md:grid-cols-3"
        data-testid={props.testId}
      >
        {props.tiles.map((tile) => (
          <RadioGroupPrimitive.Item
            key={tile.value}
            value={tile.value}
            disabled={tile.isDisabled}
            data-testid={`${props.testId}-${tile.value}`}
            className={cn(
              "relative flex min-h-touch-target min-w-0 flex-col items-start gap-1 rounded-md border-2 border-line bg-surface p-3 text-left",
              "data-[state=checked]:border-primary",
              "disabled:cursor-not-allowed disabled:border-disabled-border disabled:bg-disabled-bg disabled:opacity-60",
              "transition-colors duration-100 ease-standard",
              FOCUS_RING,
              REDUCED_MOTION_SAFE,
            )}
          >
            <span aria-hidden="true">{tile.icon}</span>
            <span className="text-body-strong text-ink break-words">
              {tile.title}
            </span>
            <span className="text-caption text-ink-secondary break-words">
              {tile.subtitle}
            </span>
            <RadioGroupPrimitive.Indicator
              className="absolute right-2 top-2 grid h-5 w-5 place-items-center rounded-full bg-primary-solid text-on-primary"
              data-testid={`${props.testId}-${tile.value}-check`}
            >
              <svg
                width={12}
                height={12}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={3.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="m5 13 4 4L19 7" />
              </svg>
            </RadioGroupPrimitive.Indicator>
          </RadioGroupPrimitive.Item>
        ))}
      </RadioGroupPrimitive.Root>
    </div>
  );
};
